/**
 * Edit modal for a dashboard widget: name, icon, and type-specific config.
 */
import { useState, useEffect } from 'react';
import { getStatusIcon, STATUS_ICON_OPTIONS } from '../../lib/statusIcons';
import { useWidgetCatalog } from '../../lib/useWidgetCatalog';
import type { StatusTimeView, WidgetFilterConfig, WidgetScope } from '../../lib/widgetScope';
import type { AgentStatusFilter } from '../../lib/agentStatusFilter';
import { WIDGET_DATE_RANGE_OPTIONS, type WidgetDateRange } from '../../lib/widgetDateRange';
import WidgetScopeFields from './WidgetScopeFields';

export type WidgetConfig = WidgetFilterConfig;

const CALL_WIDGET_TYPES = new Set([
  'abandoned-in-queue',
  'abandoned-calls-table',
  'call-metrics',
  'call-wrap-pie',
  'call-hourly-chart',
]);

const OUTBOUND_ANALYTICS_WIDGET_TYPES = new Set([
  'outbound-contact-attempts',
  'outbound-callback-rate',
]);

function initialScope(config: WidgetFilterConfig): WidgetScope {
  if (config.scope) return config.scope;
  if (config.campaign_id) return 'campaign';
  if (config.ipscape_agent_id) return 'agent';
  return 'all';
}

interface WidgetEditModalProps {
  widgetId: string;
  widgetType: string;
  title: string;
  config: WidgetConfig;
  onSave: (title: string, config: WidgetConfig) => void;
  onClose: () => void;
}

export default function WidgetEditModal({ widgetId, widgetType, title, config, onSave, onClose }: WidgetEditModalProps) {
  const { campaignOptions, teams, agents: catalogAgents, loading: catalogLoading } = useWidgetCatalog();
  const [editTitle, setEditTitle] = useState(title);
  const [editIcon, setEditIcon] = useState(
    config.icon ??
      (widgetType === 'abandoned-in-queue' || widgetType === 'abandoned-calls-table'
        ? 'PhoneOff'
        : widgetType === 'agent-presence'
          ? 'Users'
          : widgetType === 'agent-status-time' ||
              widgetType === 'outbound-contact-attempts' ||
              widgetType === 'outbound-callback-rate'
            ? 'BarChart3'
            : widgetType === 'call-metrics'
            ? 'Phone'
            : widgetType === 'call-wrap-pie'
              ? 'TrendingUp'
              : widgetType === 'call-hourly-chart'
                ? 'BarChart3'
                : 'LayoutList')
  );
  const [editScope, setEditScope] = useState<WidgetScope>(() => initialScope(config));
  const [editCampaignId, setEditCampaignId] = useState(config.campaign_id ?? '');
  const [editAgentId, setEditAgentId] = useState(config.ipscape_agent_id ?? '');
  const [editDateRange, setEditDateRange] = useState<WidgetDateRange>(
    (config.date_range as WidgetDateRange) ?? 'today'
  );
  const [editFilterType, setEditFilterType] = useState<'team' | 'campaign' | 'agent'>(
    (config.filter_type as 'team' | 'campaign' | 'agent') ?? 'campaign'
  );
  const [editFilterValue, setEditFilterValue] = useState(() =>
    config.filter_type === 'agent' && config.ipscape_agent_id
      ? config.ipscape_agent_id
      : (config.filter_value ?? '')
  );
  const [editStatusFilter, setEditStatusFilter] = useState<AgentStatusFilter>(
    (config.status_filter as AgentStatusFilter) ?? 'all'
  );
  const [editShowCampaigns, setEditShowCampaigns] = useState(config.show_campaigns === true);
  const [editChartStack, setEditChartStack] = useState<'inbound_outbound' | 'wrap_code'>(
    config.chart_stack === 'wrap_code' ? 'wrap_code' : 'inbound_outbound'
  );
  const [editTimeView, setEditTimeView] = useState<StatusTimeView>(
    config.time_view === 'pie' ? 'pie' : 'bar'
  );
  const [editTimeDetail, setEditTimeDetail] = useState(config.time_detail !== false);

  useEffect(() => {
    setEditTitle(title);
    setEditIcon(
      config.icon ??
        (widgetType === 'abandoned-in-queue' || widgetType === 'abandoned-calls-table'
          ? 'PhoneOff'
          : widgetType === 'agent-presence'
            ? 'Users'
            : widgetType === 'agent-status-time' ||
                widgetType === 'outbound-contact-attempts' ||
                widgetType === 'outbound-callback-rate'
              ? 'BarChart3'
              : widgetType === 'call-metrics'
                ? 'Phone'
                : widgetType === 'call-wrap-pie'
                  ? 'TrendingUp'
                  : widgetType === 'call-hourly-chart'
                    ? 'BarChart3'
                    : 'LayoutList')
    );
    setEditScope(initialScope(config));
    setEditCampaignId(config.campaign_id ?? '');
    setEditAgentId(config.ipscape_agent_id ?? '');
    setEditDateRange((config.date_range as WidgetDateRange) ?? 'today');
    setEditFilterType((config.filter_type as 'team' | 'campaign' | 'agent') ?? 'campaign');
    const fv =
      config.filter_type === 'agent' && config.ipscape_agent_id
        ? config.ipscape_agent_id
        : config.filter_type === 'campaign' && config.campaign_id
          ? config.campaign_id
          : (config.filter_value ?? '');
    setEditFilterValue(fv);
    setEditStatusFilter((config.status_filter as AgentStatusFilter) ?? 'all');
    setEditShowCampaigns(config.show_campaigns === true);
    setEditChartStack(config.chart_stack === 'wrap_code' ? 'wrap_code' : 'inbound_outbound');
    setEditTimeView(config.time_view === 'pie' ? 'pie' : 'bar');
    setEditTimeDetail(config.time_detail !== false);
  }, [widgetId, title, config, widgetType]);

  const applyScopeToConfig = (newConfig: WidgetConfig) => {
    newConfig.scope = editScope;
    delete newConfig.campaign_id;
    delete newConfig.ipscape_agent_id;
    if (editScope === 'campaign' && editCampaignId.trim()) {
      newConfig.campaign_id = editCampaignId.trim();
    }
    if (editScope === 'agent' && editAgentId.trim()) {
      newConfig.ipscape_agent_id = editAgentId.trim();
    }
    if (CALL_WIDGET_TYPES.has(widgetType)) {
      newConfig.date_range = editDateRange;
      delete newConfig.date_from;
      delete newConfig.date_to;
    }
    if (widgetType === 'call-hourly-chart') {
      newConfig.chart_stack = editChartStack;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newConfig: WidgetConfig = { ...config, icon: editIcon };
    if (CALL_WIDGET_TYPES.has(widgetType)) {
      applyScopeToConfig(newConfig);
    }
    if (widgetType === 'agent-presence') {
      newConfig.filter_type = editFilterType;
      delete newConfig.campaign_id;
      delete newConfig.ipscape_agent_id;
      if (editFilterType === 'campaign') {
        const opt = campaignOptions.find((c) => String(c.campaign_id) === editFilterValue);
        newConfig.filter_value = opt?.title ?? (editFilterValue.trim() || undefined);
        newConfig.campaign_id = editFilterValue.trim() || undefined;
      } else if (editFilterType === 'agent') {
        const opt = catalogAgents.find((a) => String(a.agent_id) === editFilterValue);
        newConfig.ipscape_agent_id = editFilterValue.trim() || undefined;
        newConfig.filter_value = opt?.display_name ?? undefined;
      } else {
        newConfig.filter_value = editFilterValue.trim() || undefined;
      }
      newConfig.status_filter = editStatusFilter;
      if (editShowCampaigns) newConfig.show_campaigns = true;
      else delete newConfig.show_campaigns;
    }
    if (widgetType === 'agent-status-time') {
      newConfig.filter_type = editFilterType;
      delete newConfig.campaign_id;
      delete newConfig.ipscape_agent_id;
      if (editFilterType === 'campaign') {
        const opt = campaignOptions.find((c) => String(c.campaign_id) === editFilterValue);
        newConfig.filter_value = opt?.title ?? (editFilterValue.trim() || undefined);
        newConfig.campaign_id = editFilterValue.trim() || undefined;
      } else if (editFilterType === 'agent') {
        const opt = catalogAgents.find((a) => String(a.agent_id) === editFilterValue);
        newConfig.ipscape_agent_id = editFilterValue.trim() || undefined;
        newConfig.filter_value = opt?.display_name ?? undefined;
      } else {
        newConfig.filter_value = editFilterValue.trim() || undefined;
      }
      newConfig.date_range = editDateRange;
      delete newConfig.date_from;
      delete newConfig.date_to;
      newConfig.time_view = editTimeView;
      if (editTimeDetail) newConfig.time_detail = true;
      else delete newConfig.time_detail;
    }
    if (OUTBOUND_ANALYTICS_WIDGET_TYPES.has(widgetType)) {
      newConfig.date_range = editDateRange;
      delete newConfig.date_from;
      delete newConfig.date_to;
      delete newConfig.filter_type;
      delete newConfig.filter_value;
      delete newConfig.campaign_id;
      delete newConfig.ipscape_agent_id;
      delete newConfig.scope;
    }
    onSave(editTitle.trim() || title, newConfig);
    onClose();
  };

  const IconComponent = getStatusIcon(editIcon);
  const showCallScope = CALL_WIDGET_TYPES.has(widgetType);
  const showAnalyticsDateRange = OUTBOUND_ANALYTICS_WIDGET_TYPES.has(widgetType);

  const selectStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.5rem 0.75rem',
    borderRadius: 6,
    border: '1px solid var(--header-border)',
    background: 'var(--bg)',
    color: 'var(--text)',
    fontSize: '0.875rem',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="widget-edit-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: 'var(--widget-bg)',
          borderRadius: 12,
          border: '1px solid var(--header-border)',
          padding: '1.5rem',
          maxWidth: 420,
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="widget-edit-title" style={{ margin: '0 0 1rem', fontSize: '1.125rem' }}>
          Edit widget
        </h2>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="widget-name" style={{ display: 'block', marginBottom: 4, fontSize: '0.875rem', color: 'var(--text-muted)' }}>
              Name
            </label>
            <input
              id="widget-name"
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              style={selectStyle}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="widget-icon" style={{ display: 'block', marginBottom: 4, fontSize: '0.875rem', color: 'var(--text-muted)' }}>
              Icon
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <IconComponent size={20} style={{ color: 'var(--accent)' }} />
              <select id="widget-icon" value={editIcon} onChange={(e) => setEditIcon(e.target.value)} style={{ ...selectStyle, flex: 1, minWidth: 160 }}>
                {STATUS_ICON_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {widgetType === 'agent-status-time' && (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="widget-time-filter-type" style={{ display: 'block', marginBottom: 4, fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                  Show by
                </label>
                <select
                  id="widget-time-filter-type"
                  value={editFilterType}
                  onChange={(e) => {
                    setEditFilterType(e.target.value as 'team' | 'campaign' | 'agent');
                    setEditFilterValue('');
                  }}
                  style={selectStyle}
                >
                  <option value="team">Team</option>
                  <option value="campaign">Campaign</option>
                  <option value="agent">Individual agent</option>
                </select>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="widget-time-filter-value" style={{ display: 'block', marginBottom: 4, fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                  {editFilterType === 'team' ? 'Team' : editFilterType === 'campaign' ? 'Campaign' : 'Agent'}
                </label>
                <select
                  id="widget-time-filter-value"
                  value={editFilterValue}
                  onChange={(e) => setEditFilterValue(e.target.value)}
                  style={selectStyle}
                  disabled={catalogLoading}
                >
                  <option value="">— Select —</option>
                  {editFilterType === 'team'
                    ? teams.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))
                    : editFilterType === 'agent'
                      ? catalogAgents.map((a) => (
                          <option key={a.agent_id} value={String(a.agent_id)}>
                            {a.display_name}
                          </option>
                        ))
                      : campaignOptions.map((c) =>
                          c.campaign_id != null ? (
                            <option key={c.campaign_id} value={String(c.campaign_id)}>
                              {c.title}
                            </option>
                          ) : null
                        )}
                </select>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="widget-time-date-range" style={{ display: 'block', marginBottom: 4, fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                  Date range
                </label>
                <select
                  id="widget-time-date-range"
                  value={editDateRange}
                  onChange={(e) => setEditDateRange(e.target.value as WidgetDateRange)}
                  style={selectStyle}
                >
                  {WIDGET_DATE_RANGE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="widget-time-view" style={{ display: 'block', marginBottom: 4, fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                  Display as
                </label>
                <select
                  id="widget-time-view"
                  value={editTimeView}
                  onChange={(e) => setEditTimeView(e.target.value as StatusTimeView)}
                  style={selectStyle}
                >
                  <option value="pie">Pie chart</option>
                  <option value="bar">Horizontal stacked bar</option>
                </select>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editTimeDetail}
                    onChange={(e) => setEditTimeDetail(e.target.checked)}
                  />
                  Show pause reasons &amp; sub-states
                </label>
                <p style={{ margin: '0.35rem 0 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  For an individual agent, splits Paused / Unavailable (etc.) by UC pause reason — e.g. ACW, Ad Hoc
                  Break, Admin Work. Team/campaign views stay summary-only.
                </p>
              </div>
            </>
          )}

          {widgetType === 'agent-presence' && (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="widget-filter-type" style={{ display: 'block', marginBottom: 4, fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                  Show by
                </label>
                <select
                  id="widget-filter-type"
                  value={editFilterType}
                  onChange={(e) => {
                    setEditFilterType(e.target.value as 'team' | 'campaign' | 'agent');
                    setEditFilterValue('');
                  }}
                  style={selectStyle}
                >
                  <option value="team">Team</option>
                  <option value="campaign">Campaign</option>
                  <option value="agent">Individual agent</option>
                </select>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="widget-filter-value" style={{ display: 'block', marginBottom: 4, fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                  {editFilterType === 'team' ? 'Team' : editFilterType === 'campaign' ? 'Campaign' : 'Agent'}
                </label>
                <select
                  id="widget-filter-value"
                  value={editFilterValue}
                  onChange={(e) => setEditFilterValue(e.target.value)}
                  style={selectStyle}
                  disabled={catalogLoading}
                >
                  <option value="">— Select —</option>
                  {editFilterType === 'team'
                    ? teams.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))
                    : editFilterType === 'agent'
                      ? catalogAgents.map((a) => (
                          <option key={a.agent_id} value={String(a.agent_id)}>
                            {a.display_name}
                          </option>
                        ))
                      : campaignOptions.map((c) =>
                          c.campaign_id != null ? (
                            <option key={c.campaign_id} value={String(c.campaign_id)}>
                              {c.title}
                            </option>
                          ) : null
                        )}
                </select>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="widget-status-filter" style={{ display: 'block', marginBottom: 4, fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                  Default status filter
                </label>
                <select
                  id="widget-status-filter"
                  value={editStatusFilter}
                  onChange={(e) => setEditStatusFilter(e.target.value as AgentStatusFilter)}
                  style={selectStyle}
                >
                  <option value="all">All</option>
                  <option value="available">Available only</option>
                  <option value="unavailable">Unavailable only</option>
                </select>
                <p style={{ margin: '0.35rem 0 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  You can also switch All / Available / Unavailable on the widget while viewing.
                </p>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editShowCampaigns}
                    onChange={(e) => setEditShowCampaigns(e.target.checked)}
                  />
                  Show campaign names
                </label>
                <p style={{ margin: '0.35rem 0 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  Uses catalog titles (not Q-codes). Off by default for a cleaner single-agent tile.
                </p>
              </div>
            </>
          )}

          {widgetType === 'call-hourly-chart' && (
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="widget-chart-stack" style={{ display: 'block', marginBottom: 4, fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                Stack bars by
              </label>
              <select
                id="widget-chart-stack"
                value={editChartStack}
                onChange={(e) => setEditChartStack(e.target.value as 'inbound_outbound' | 'wrap_code')}
                style={selectStyle}
              >
                <option value="inbound_outbound">Inbound / Outbound</option>
                <option value="wrap_code">Wrap codes</option>
              </select>
              <p style={{ margin: '0.35rem 0 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                Wrap mode shows the top wrap outcomes per hour (or per day on This Week). Hover bars for counts.
              </p>
            </div>
          )}

          {showAnalyticsDateRange && (
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="widget-analytics-date-range" style={{ display: 'block', marginBottom: 4, fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                Period
              </label>
              <select
                id="widget-analytics-date-range"
                value={editDateRange}
                onChange={(e) => setEditDateRange(e.target.value as WidgetDateRange)}
                style={selectStyle}
              >
                {WIDGET_DATE_RANGE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p style={{ margin: '0.35rem 0 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                Organisation-wide outbound metrics. Admin access required. Data is retained after call history purge.
              </p>
            </div>
          )}

          {showCallScope && (
            <WidgetScopeFields
              scope={editScope}
              onScopeChange={setEditScope}
              campaignId={editCampaignId}
              onCampaignIdChange={setEditCampaignId}
              ipscapeAgentId={editAgentId}
              onAgentIdChange={setEditAgentId}
              dateRange={editDateRange}
              onDateRangeChange={setEditDateRange}
            />
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: 6,
                border: '1px solid var(--header-border)',
                background: 'var(--widget-bg)',
                color: 'var(--text)',
                cursor: 'pointer',
                fontSize: '0.875rem',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: '0.5rem 1rem',
                borderRadius: 6,
                border: 'none',
                background: 'var(--accent)',
                color: 'white',
                cursor: 'pointer',
                fontSize: '0.875rem',
              }}
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
